import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  User, 
  HardDrive, 
  Calendar, 
  CreditCard, 
  Crown, 
  Zap, 
  AlertTriangle,
  ExternalLink,
  LogOut,
  Upload,
  Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import UserRegistrationModal from '../components/UserRegistrationModal';
import CheckoutModal from '../components/CheckoutModal';
import BandwidthUsageModal from '../components/BandwidthUsageModal';
import { getOriginalUserId } from '../utils/api';

// Utility functions
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const gb = bytes / (1024 * 1024 * 1024);
  return `${Math.round(gb)}GB`;
};

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [bandwidthUsage, setBandwidthUsage] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showBandwidthModal, setShowBandwidthModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchProfileData();
    } else {
      // For non-authenticated users, still fetch bandwidth usage
      fetchBandwidthUsageOnly();
    }
  }, [isAuthenticated, user]);

  const fetchBandwidthUsageOnly = async () => {
    try {
      // Import getUserId function
      const { getUserId } = await import('../utils/api');
      const userId = getUserId();
      console.log('📊 fetchBandwidthUsageOnly called with userId:', userId);
      
      if (!userId) {
        setLoading(false);
        return;
      }

      const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
      const apiUrl = `${apiBase}/api/ip-usage?userId=${encodeURIComponent(userId)}`;
      console.log('📊 Fetching bandwidth from:', apiUrl);
      
      // Fetch bandwidth usage for anonymous users
      const bandwidthResponse = await fetch(apiUrl);
      console.log('📊 Bandwidth response status:', bandwidthResponse.status);
      
      if (bandwidthResponse.ok) {
        const bandwidthData = await bandwidthResponse.json();
        console.log('📊 Bandwidth data received:', bandwidthData);
        setBandwidthUsage(bandwidthData);
      } else {
        console.error('📊 Bandwidth fetch failed:', await bandwidthResponse.text());
      }

      // Fetch storage usage for anonymous users too
      const storageUrl = `${apiBase}/api/user/storage?userId=${encodeURIComponent(userId)}`;
      console.log('📊 Fetching storage from:', storageUrl);
      
      const storageResponse = await fetch(storageUrl);
      console.log('📊 Storage response status:', storageResponse.status);
      
      if (storageResponse.ok) {
        const storageData = await storageResponse.json();
        console.log('📊 Storage data received:', storageData);
        
        // Set profile with storage data for anonymous users
        setProfile({
          storageUsage: storageData.storageUsage,
          storageUsagePercent: storageData.storageUsagePercent,
          subscriptionTier: storageData.subscriptionTier
        });
      } else {
        console.error('📊 Storage fetch failed:', await storageResponse.text());
      }
    } catch (e) {
      console.error('Error fetching bandwidth usage:', e);
    } finally {
      setLoading(false);
    }
  };

  // On return from payment, auto-activate if status=active in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const subscriptionId = params.get('subscription_id');
    if (status === 'active' || subscriptionId) {
      (async () => {
        try {
          const userId = user?.uid ? `user_${user.uid}` : localStorage.getItem('fileshare_user_id');
          const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
          // Trigger activation once
          await fetch(`${apiBase}/api/subscription/activate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subscriptionId, userId })
          });
          // Poll status for up to ~15s to reflect change
          const start = Date.now();
          while (Date.now() - start < 15000) {
            await fetchProfileData();
            if (profile?.subscriptionTier?.status === 'active') break;
            await new Promise(r => setTimeout(r, 1500));
          }
        } catch (_) {}
      })();
    }
  }, []);

  const fetchProfileData = async () => {
    try {
      // Import getUserId function
      const { getUserId } = await import('../utils/api');
      const userId = getUserId();
      console.log('📊 fetchProfileData called with userId:', userId);
      
      if (!userId) {
        setLoading(false);
        return;
      }

      const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
      
      // Fetch user profile and subscription details
      const profileResponse = await fetch(`${apiBase}/api/user/profile?userId=${userId}`);
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        console.log('📊 Profile data received:', profileData);
        setProfile(profileData.profile);
      }

      // Fetch bandwidth usage
      const bandwidthUrl = `${apiBase}/api/ip-usage?userId=${encodeURIComponent(userId)}`;
      console.log('📊 Fetching bandwidth from:', bandwidthUrl);
      
      const bandwidthResponse = await fetch(bandwidthUrl);
      console.log('📊 Bandwidth response status:', bandwidthResponse.status);
      
      if (bandwidthResponse.ok) {
        const bandwidthData = await bandwidthResponse.json();
        console.log('📊 Bandwidth data received:', bandwidthData);
        setBandwidthUsage(bandwidthData);
      }

      // Fetch storage usage
      const storageUrl = `${apiBase}/api/user/storage?userId=${encodeURIComponent(userId)}`;
      console.log('📊 Fetching storage from:', storageUrl);
      
      const storageResponse = await fetch(storageUrl);
      console.log('📊 Storage response status:', storageResponse.status);
      
      if (storageResponse.ok) {
        const storageData = await storageResponse.json();
        console.log('📊 Storage data received:', storageData);
        
        // Update profile with storage data
        setProfile(prevProfile => ({
          ...prevProfile,
          storageUsage: storageData.storageUsage,
          storageUsagePercent: storageData.storageUsagePercent,
          subscriptionTier: storageData.subscriptionTier
        }));
      }

    } catch (e) {
      console.error('Error fetching profile data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = (planId) => {
    console.log('handleUpgrade called with planId:', planId);
    console.log('isAuthenticated:', isAuthenticated);
    console.log('user:', user);
    console.log('showCheckoutModal before:', showCheckoutModal);
    console.log('showRegistrationModal before:', showRegistrationModal);
    
    if (!isAuthenticated || !user) {
      console.log('User not authenticated, showing registration modal');
      setSelectedPlanId(planId);
      setShowRegistrationModal(true);
      return;
    }
    
    console.log('User authenticated, showing checkout modal');
    setSelectedPlanId(planId);
    setShowCheckoutModal(true);
    console.log('showCheckoutModal after:', showCheckoutModal);
  };

  const handleLogout = async () => {
    try {
      // Import Firebase auth functions
      const { signOutUser } = await import('../firebase/auth');
      await signOutUser();
      
      // Clear local storage
      localStorage.removeItem('fileshare_user_id');
      
      // Clear profile data
      setProfile(null);
      setBandwidthUsage(null);
      
      console.log('User logged out successfully');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleRegistrationSuccess = (firebaseUser) => {
    setShowRegistrationModal(false);
    setShowCheckoutModal(true);
  };

  const handleCheckoutClose = () => {
    setShowCheckoutModal(false);
    setSelectedPlanId(null);
    // Refresh profile data after potential subscription change
    fetchProfileData();
  };

  const handleActivateSubscription = async () => {
    if (!isAuthenticated || !user) return;
    
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
      const response = await fetch(`${apiBase}/api/subscription/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid })
      });
      
      if (response.ok) {
        // Refresh profile data
        await fetchProfileData();
        alert('Subscription activated successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to activate subscription: ${error.error}`);
      }
    } catch (error) {
      console.error('Error activating subscription:', error);
      alert('Failed to activate subscription');
    }
  };

  const handleCancelSubscription = async () => {
    try {
      if (!isAuthenticated || !user) return;
      const confirmed = window.confirm('Are you sure you want to cancel your subscription? This will stop future renewals.');
      if (!confirmed) return;

      const userId = user?.uid ? `user_${user.uid}` : localStorage.getItem('fileshare_user_id');
      const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
      const res = await fetch(`${apiBase}/api/subscription/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to cancel subscription');
      alert('Your subscription has been cancelled.');
      fetchProfileData();
    } catch (e) {
      console.error('Cancel subscription error:', e);
      alert(e.message || 'Failed to cancel subscription');
    }
  };

  const handleRenew = () => {
    if (!isAuthenticated || !user) return;
    const currentPlan = profile?.subscriptionTier?.tier;
    const planId = 'premium';
    setSelectedPlanId(planId);
    setShowCheckoutModal(true);
  };

  const getSubscriptionBadge = (subscriptionTier) => {
    const tier = subscriptionTier?.tier || 'free';
    const displayName = subscriptionTier?.displayName || subscriptionTier?.name || 'Free';
    
    switch (tier) {
      case 'free':
        return (
          <div style={{ 
            backgroundColor: '#374151', 
            color: '#9ca3af', 
            padding: '4px 8px', 
            borderRadius: '3px', 
            fontSize: '12px', 
            fontWeight: '600' 
          }}>
            {displayName}
          </div>
        );
      case 'basic':
      case 'premium':
      case 'pro':
        return (
          <div style={{ 
            backgroundColor: '#16a34a', 
            color: 'white', 
            padding: '4px 8px', 
            borderRadius: '3px', 
            fontSize: '12px', 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Crown style={{ height: '12px', width: '12px' }} />
            {displayName}
          </div>
        );
      default:
        return (
          <div style={{ 
            backgroundColor: '#374151', 
            color: '#9ca3af', 
            padding: '4px 8px', 
            borderRadius: '3px', 
            fontSize: '12px', 
            fontWeight: '600' 
          }}>
            Free
          </div>
        );
    }
  };

  const getSubscriptionStatus = (status) => {
    console.log('getSubscriptionStatus called with status:', status);
    
    switch (status) {
      case 'active':
        return (
          <div style={{ 
            backgroundColor: '#059669', 
            color: 'white', 
            padding: '4px 8px', 
            borderRadius: '3px', 
            fontSize: '12px', 
            fontWeight: '600' 
          }}>
            Active
          </div>
        );
      case 'pending':
        return (
          <div style={{ 
            backgroundColor: '#f59e0b', 
            color: 'white', 
            padding: '4px 8px', 
            borderRadius: '3px', 
            fontSize: '12px', 
            fontWeight: '600' 
          }}>
            Pending
          </div>
        );
      case 'grace_period':
        return (
          <div style={{ 
            backgroundColor: '#dc2626', 
            color: 'white', 
            padding: '4px 8px', 
            borderRadius: '3px', 
            fontSize: '12px', 
            fontWeight: '600' 
          }}>
            Grace Period
          </div>
        );
      case 'expired':
        return (
          <div style={{ 
            backgroundColor: '#dc2626', 
            color: 'white', 
            padding: '4px 8px', 
            borderRadius: '3px', 
            fontSize: '12px', 
            fontWeight: '600' 
          }}>
            Expired
          </div>
        );
      case 'cancelled':
        return (
          <div style={{ 
            backgroundColor: '#374151', 
            color: '#9ca3af', 
            padding: '4px 8px', 
            borderRadius: '3px', 
            fontSize: '12px', 
            fontWeight: '600' 
          }}>
            Cancelled
          </div>
        );
      case null:
      case undefined:
      case '':
        // Default to Active for premium users with no explicit status
        return (
          <div style={{ 
            backgroundColor: '#059669', 
            color: 'white', 
            padding: '4px 8px', 
            borderRadius: '3px', 
            fontSize: '12px', 
            fontWeight: '600' 
          }}>
            Active
          </div>
        );
      default:
        console.log('Unknown subscription status:', status);
        return (
          <div style={{ 
            backgroundColor: '#059669', 
            color: 'white', 
            padding: '4px 8px', 
            borderRadius: '3px', 
            fontSize: '12px', 
            fontWeight: '600' 
          }}>
            Active
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#1a1d29', color: '#e2e8f0', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '3px solid #374151', 
            borderTop: '3px solid #3b82f6', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#1a1d29', color: '#e2e8f0', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '64px 16px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <h1 style={{ fontSize: '20px', fontWeight: '600', color: '#e2e8f0', marginBottom: '8px', letterSpacing: '-0.02em' }}>Profile</h1>
            <p style={{ fontSize: '12px', color: '#9ca3af' }}>Manage your account and subscription</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '32px' }}>
            
            {/* User Information */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <User style={{ height: '20px', width: '20px', color: '#3b82f6' }} />
                <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>Account Information</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {isAuthenticated && user ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>Email</span>
                      <span style={{ fontSize: '14px', color: '#9ca3af' }}>{user.email || 'Not set'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>Display Name</span>
                      <span style={{ fontSize: '14px', color: '#9ca3af' }}>{user.displayName || 'Not set'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>User ID</span>
                      <span style={{ fontSize: '14px', color: '#9ca3af', fontFamily: 'monospace' }}>{user.uid}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>Status</span>
                      <div style={{ 
                        backgroundColor: '#10b981', 
                        color: 'white', 
                        padding: '4px 8px', 
                        borderRadius: '3px', 
                        fontSize: '12px', 
                        fontWeight: '600' 
                      }}>
                        Authenticated
                      </div>
                    </div>
                    {profile?.created_at && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>Member Since</span>
                        <span style={{ fontSize: '14px', color: '#9ca3af' }}>
                          {new Date(profile.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    
                    {/* Logout Button */}
                    <div style={{ paddingTop: '16px', borderTop: '1px solid #374151' }}>
                      <button 
                        onClick={handleLogout}
                        style={{ 
                          backgroundColor: '#dc2626', 
                          color: 'white', 
                          padding: '10px 16px', 
                          borderRadius: '3px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <LogOut style={{ height: '14px', width: '14px' }} />
                        Logout
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <p style={{ color: '#9ca3af', marginBottom: '16px', fontSize: '14px' }}>Upgrade to Premium for account management</p>
                    <button 
                      onClick={() => {
                        console.log('Upgrade button clicked (non-auth)!');
                        handleUpgrade('premium');
                      }}
                      style={{ 
                        backgroundColor: '#16a34a', 
                        color: 'white', 
                        padding: '12px 24px', 
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '700',
                        width: '100%',
                        transition: 'all 0.2s ease',
                        transform: 'translateY(0)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#15803d';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#16a34a';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      Upgrade to Premium
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Subscription Information */}
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <CreditCard style={{ height: '20px', width: '20px', color: '#3b82f6' }} />
                <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>Subscription</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {isAuthenticated && user ? (
                  profile?.subscriptionTier ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>Current Plan</span>
                        {getSubscriptionBadge(profile.subscriptionTier)}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>Status</span>
                        {getSubscriptionStatus(profile.subscriptionTier.status)}
                      </div>
                      {profile.subscription_expires_at && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>Expires</span>
                          <span style={{ fontSize: '14px', color: '#9ca3af' }}>
                            {new Date(profile.subscription_expires_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {/* Paid: show unlimited file size */}
                      {profile.subscriptionTier.tier !== 'free' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>File Size</span>
                          <span style={{ fontSize: '14px', color: '#9ca3af' }}>Unlimited</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>File Expiry</span>
                        <span style={{ fontSize: '14px', color: '#9ca3af' }}>
                          {profile.subscriptionTier.tier !== 'free' ? 'No expiry (Premium)' : `${profile.subscriptionTier.expiryDays} days`}
                        </span>
                      </div>
                      
                      {/* Activation button for pending subscriptions */}
                      {profile.subscriptionTier.status === 'pending' && (
                        <div style={{ paddingTop: '16px', borderTop: '1px solid #374151' }}>
                          <button
                            onClick={handleActivateSubscription}
                            style={{
                              backgroundColor: '#16a34a',
                              color: 'white',
                              padding: '12px 24px',
                              borderRadius: '3px',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '600',
                              width: '100%',
                              transition: 'all 0.2s ease',
                              transform: 'translateY(0)',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#15803d';
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#16a34a';
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                            }}
                          >
                            Activate Subscription
                          </button>
                          <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px', textAlign: 'center' }}>
                            Your payment was successful but the subscription needs to be activated. This will verify your payment with the payment processor.
                          </p>
                        </div>
                      )}

                      {/* Upgrade/Cancel/Renew Options */}
                      {profile.subscriptionTier.tier === 'free' ? (
                        <div style={{ paddingTop: '16px', borderTop: '1px solid #374151' }}>
                          <button 
                            onClick={() => {
                              console.log('Upgrade button clicked!');
                              handleUpgrade('premium');
                            }}
                            style={{ 
                              backgroundColor: '#16a34a', 
                              color: 'white', 
                              padding: '12px 24px', 
                              borderRadius: '6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '700',
                              width: '100%',
                              transition: 'all 0.2s ease',
                              transform: 'translateY(0)',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#15803d';
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#16a34a';
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                            }}
                          >
                            Upgrade to Premium - $8/month
                          </button>
                        </div>
                      ) : (
                        <div style={{ paddingTop: '16px', borderTop: '1px solid #374151' }}>
                          <button
                            onClick={handleCancelSubscription}
                            style={{
                              backgroundColor: '#374151',
                              color: '#9ca3af',
                              padding: '10px 16px',
                              borderRadius: '3px',
                              border: '1px solid #4b5563',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '600',
                              width: '100%',
                              opacity: '0.7',
                              transition: 'all 0.2s ease',
                              transform: 'translateY(0)',
                              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#4b5563';
                              e.target.style.color = '#d1d5db';
                              e.target.style.borderColor = '#6b7280';
                              e.target.style.opacity = '1';
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#374151';
                              e.target.style.color = '#9ca3af';
                              e.target.style.borderColor = '#4b5563';
                              e.target.style.opacity = '0.7';
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)';
                            }}
                          >
                            Cancel Subscription
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                      <p style={{ color: '#9ca3af', marginBottom: '16px', fontSize: '14px' }}>Loading subscription details...</p>
                    </div>
                  )
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <p style={{ color: '#9ca3af', marginBottom: '16px', fontSize: '14px' }}>Upgrade to Premium to view subscription details</p>
                    <button 
                      onClick={() => {
                        console.log('Upgrade button clicked (loading)!');
                        handleUpgrade('premium');
                      }}
                      style={{ 
                        backgroundColor: '#16a34a', 
                        color: 'white', 
                        padding: '12px 24px', 
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '700',
                        width: '100%',
                        transition: 'all 0.2s ease',
                        transform: 'translateY(0)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#15803d';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#16a34a';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      Upgrade to Premium
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Storage Usage (premium only) */}
          {profile?.subscriptionTier && profile.subscriptionTier.tier !== 'free' && (
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '24px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <HardDrive style={{ height: '20px', width: '20px', color: '#3b82f6' }} />
                <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>Storage Usage</h3>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>Used</span>
                  <span style={{ fontSize: '14px', color: '#9ca3af' }}>
                    {formatBytes(profile?.storageUsage || 0)} / {formatBytes(profile?.subscriptionTier?.storageQuota || 0)}
                  </span>
                </div>
                {(() => {
                  const usage = profile?.storageUsage || 0;
                  const quota = profile?.subscriptionTier?.storageQuota || 0;
                  const percent = quota > 0 ? Math.round((usage / quota) * 100) : 0;
                  return (
                    <div style={{ 
                      width: '100%', 
                      height: '10px', 
                      backgroundColor: '#1f2937', 
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{ 
                        width: `${percent}%`, 
                        height: '100%', 
                        backgroundColor: '#10b981',
                        backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0, rgba(255,255,255,0.15) 8px, rgba(255,255,255,0.08) 8px, rgba(255,255,255,0.08) 16px)',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                  );
                })()}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                    {(() => {
                      const usage = profile?.storageUsage || 0;
                      const quota = profile?.subscriptionTier?.storageQuota || 0;
                      const percent = quota > 0 ? Math.round((usage / quota) * 100) : 0;
                      return `${percent}% used`;
                    })()}
                  </span>
                  {(() => {
                    const usage = profile?.storageUsage || 0;
                    const quota = profile?.subscriptionTier?.storageQuota || 0;
                    const percent = quota > 0 ? Math.round((usage / quota) * 100) : 0;
                    return percent > 80;
                  })() && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#dc2626' }}>
                      <AlertTriangle style={{ height: '12px', width: '12px' }} />
                      Low storage
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Bandwidth Usage (shown for all users) */}
          {bandwidthUsage && (
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '24px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar style={{ height: '20px', width: '20px', color: '#3b82f6' }} />
                  <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>Monthly Bandwidth Usage</h3>
                </div>
                <button
                  onClick={async () => {
                    // Refresh bandwidth data before opening modal
                    await fetchBandwidthUsageOnly();
                    setShowBandwidthModal(true);
                  }}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '3px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Calendar style={{ height: '14px', width: '14px' }} />
                  View Details
                </button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>Used</span>
                <span style={{ fontSize: '14px', color: '#9ca3af' }}>
                  {formatBytes(bandwidthUsage.totalBytes)} / {formatBytes(bandwidthUsage.limitBytes)} ({bandwidthUsage.usagePercent || 0}%)
                </span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: '#374151', 
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${bandwidthUsage.usagePercent || 0}%`, 
                  height: '100%', 
                  backgroundColor: '#10b981',
                  backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0, rgba(255,255,255,0.15) 8px, rgba(255,255,255,0.08) 8px, rgba(255,255,255,0.08) 16px)',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {bandwidthUsage.usagePercent || 0}% used
                </span>
                {(bandwidthUsage.usagePercent || 0) > 80 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#dc2626' }}>
                    <AlertTriangle style={{ height: '12px', width: '12px' }} />
                    {bandwidthUsage.isPremium ? 'High usage' : 'Bandwidth limit'}
                  </div>
                )}
              </div>
              
              {/* Upload/Download Breakdown */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                fontSize: '12px',
                color: '#9ca3af',
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid #374151'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Upload style={{ height: '14px', width: '14px', color: '#3b82f6' }} />
                  <span>Upload: {formatBytes(bandwidthUsage.uploadBytes || 0)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Download style={{ height: '14px', width: '14px', color: '#10b981' }} />
                  <span>Download: {formatBytes(bandwidthUsage.downloadBytes || 0)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Storage Usage (shown for all users) */}
          {profile && profile.storageUsage !== undefined && (
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '24px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg style={{ height: '20px', width: '20px', color: '#10b981' }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                  </svg>
                  <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>Storage Usage</h3>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>Used</span>
                <span style={{ fontSize: '14px', color: '#9ca3af' }}>
                  {formatBytes(profile.storageUsage)} / {formatBytes(profile.subscriptionTier?.storageQuota || 0)} ({profile.storageUsagePercent || 0}%)
                </span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: '#374151', 
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${Math.min(profile.storageUsagePercent || 0, 100)}%`, 
                  height: '100%', 
                  backgroundColor: profile.storageUsagePercent > 80 ? '#ef4444' : '#10b981',
                  backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0, rgba(255,255,255,0.15) 8px, rgba(255,255,255,0.08) 8px, rgba(255,255,255,0.08) 16px)',
                  transition: 'width 0.3s ease'
                }}></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {profile.storageUsagePercent || 0}% used
                </span>
                {(profile.storageUsagePercent || 0) > 80 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#dc2626' }}>
                    <svg style={{ height: '12px', width: '12px' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z"/>
                    </svg>
                    {profile.subscriptionTier?.tier === 'free' ? 'Storage limit' : 'High usage'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Free User Bandwidth Info */}
          {(!isAuthenticated || (profile?.subscriptionTier && profile.subscriptionTier.tier === 'free')) && bandwidthUsage && (
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '16px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Calendar style={{ height: '16px', width: '16px', color: '#3b82f6' }} />
                <h4 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', margin: 0 }}>Free Bandwidth</h4>
              </div>
              <p style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
                You have <strong style={{ color: '#3b82f6' }}>100GB</strong> monthly bandwidth limit (uploads + downloads). 
                Bandwidth is tracked against the file uploader. When others download your files, it counts against your limit. 
                Downloading your own files is free. Upgrade to Premium for <strong style={{ color: '#16a34a' }}>1TB</strong> bandwidth.
              </p>
            </div>
          )}

          {/* Premium Bandwidth Info */}
          {profile?.subscriptionTier && profile.subscriptionTier.tier !== 'free' && (
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '16px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Calendar style={{ height: '16px', width: '16px', color: '#16a34a' }} />
                <h4 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', margin: 0 }}>Premium Bandwidth</h4>
              </div>
              <p style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
                You have <strong style={{ color: '#16a34a' }}>1TB monthly bandwidth</strong> as a Premium user (uploads + downloads). 
                Bandwidth is tracked against the file uploader. When others download your files, it counts against your limit. 
                Downloading your own files is free. Usage beyond 1TB will be charged at $0.003 per GB.
              </p>
            </div>
          )}

          {/* Free User Storage Info */}
          {(!isAuthenticated || (profile?.subscriptionTier && profile.subscriptionTier.tier === 'free')) && profile && profile.storageUsage !== undefined && (
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '16px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <svg style={{ height: '16px', width: '16px', color: '#3b82f6' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                </svg>
                <h4 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', margin: 0 }}>Free Storage</h4>
              </div>
              <p style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
                You have <strong style={{ color: '#3b82f6' }}>100GB</strong> storage limit. 
                Files expire after 5 days. Upgrade to Premium for <strong style={{ color: '#16a34a' }}>500GB</strong> permanent storage with no expiry.
              </p>
            </div>
          )}

          {/* Premium User Storage Info */}
          {profile?.subscriptionTier && profile.subscriptionTier.tier !== 'free' && profile && profile.storageUsage !== undefined && (
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '16px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <svg style={{ height: '16px', width: '16px', color: '#16a34a' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                </svg>
                <h4 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0', margin: 0 }}>Premium Storage</h4>
              </div>
              <p style={{ color: '#d1d5db', fontSize: '13px', lineHeight: '1.5', margin: 0 }}>
                You have <strong style={{ color: '#16a34a' }}>500GB</strong> permanent storage with no expiry. 
                Usage beyond 500GB will be charged at $0.016 per GB.
              </p>
            </div>
          )}

          {/* Metered Billing Info (premium only) */}
          {profile?.subscriptionTier && profile.subscriptionTier.tier !== 'free' && (
            <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '24px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <CreditCard style={{ height: '20px', width: '20px', color: '#16a34a' }} />
                <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>Metered Billing</h3>
              </div>
              <div style={{ color: '#d1d5db', fontSize: '14px', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 8px 0' }}>
                  <strong style={{ color: '#16a34a' }}>Storage overage:</strong> $0.016 per GB (charged monthly)
                </p>
                <p style={{ margin: 0 }}>
                  <strong style={{ color: '#16a34a' }}>Bandwidth overage:</strong> $0.003 per GB (charged monthly)
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#9ca3af' }}>
                  Usage is tracked automatically and overages are added to your next billing cycle.
                </p>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#e2e8f0', marginBottom: '16px' }}>Quick Actions</h3>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Link 
                to="/"
                style={{ 
                  backgroundColor: '#3b82f6', 
                  color: 'white', 
                  padding: '8px 16px', 
                  borderRadius: '3px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Upload Files
              </Link>
              <Link 
                to="/uploads"
                style={{ 
                  backgroundColor: '#374151', 
                  color: '#e2e8f0', 
                  padding: '8px 16px', 
                  borderRadius: '3px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: '1px solid #4b5563'
                }}
              >
                My Files
              </Link>
              <Link 
                to="/pricing"
                style={{ 
                  backgroundColor: '#374151', 
                  color: '#e2e8f0', 
                  padding: '8px 16px', 
                  borderRadius: '3px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: '1px solid #4b5563'
                }}
              >
                View Plans
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <UserRegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        onSuccess={handleRegistrationSuccess}
        currentUserId={getOriginalUserId() || localStorage.getItem('fileshare_user_id')}
      />

      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={handleCheckoutClose}
        planId={selectedPlanId}
        userEmail={user?.email}
        userId={localStorage.getItem('fileshare_user_id')}
      />

      <BandwidthUsageModal
        isOpen={showBandwidthModal}
        onClose={() => setShowBandwidthModal(false)}
        bandwidthUsage={bandwidthUsage}
      />

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}